import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

/**
 * Diaporama publicitaire / mode inactivité
 * Affiche un résumé stats du jour + slides (images, vidéos, YouTube) en boucle
 * Props: config, serverUrl, onInterrupt
 */

/**
 * URL d'INTÉGRATION YouTube à partir de ce que l'exploitant colle réellement.
 *
 * Il copie la barre d'adresse — `https://www.youtube.com/watch?v=XXXX&t=1` — et c'est
 * légitime : c'est l'URL qu'on lui montre. Or YouTube REFUSE d'être encadré depuis
 * `/watch` (en-tête `X-Frame-Options`) : l'iframe reste blanche, sans image ni son, et
 * rien n'indique pourquoi. Seule la forme `/embed/<id>` est intégrable.
 *
 * On accepte donc les trois formes rencontrées (watch, youtu.be, shorts) et on rend une
 * URL utilisable. Une adresse déjà en `/embed/` est laissée telle quelle. Si rien n'est
 * reconnu, on retourne `null` : mieux vaut sauter la slide que peindre l'écran en blanc.
 */
export function urlIntegrationYoutube(url) {
  const brut = String(url || '').trim();
  if (!brut) return null;
  if (/youtube\.com\/embed\//i.test(brut)) return brut;

  let id = null;
  let debut = null;
  try {
    const u = new URL(brut);
    const hote = u.hostname.replace(/^www\./, '');
    if (hote === 'youtu.be') id = u.pathname.slice(1);
    else if (hote.endsWith('youtube.com')) {
      id = u.searchParams.get('v') || (u.pathname.match(/\/shorts\/([\w-]+)/) || [])[1] || null;
    }
    // `t=90`, `t=1m30s` : on ne garde que les secondes entières, seule forme acceptée
    // par le paramètre `start` de l'intégration.
    const t = u.searchParams.get('t') || u.searchParams.get('start');
    if (t && /^\d+s?$/.test(t)) debut = parseInt(t, 10);
  } catch {
    return null; // pas une URL : on ne devine pas
  }
  if (!id || !/^[\w-]{6,}$/.test(id)) return null;

  const params = new URLSearchParams({ autoplay: '1', mute: '1', rel: '0', playsinline: '1' });
  if (debut) params.set('start', String(debut));
  return `https://www.youtube.com/embed/${id}?${params.toString()}`;
}

const AdSlideshow = ({ config, serverUrl = '', onInterrupt }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [stats, setStats] = useState(null);
  const [transitioning, setTransitioning] = useState(false);
  const timerRef = useRef(null);
  const videoRef = useRef(null);

  const advertising = config?.advertising || {};
  const transition = advertising.transition || 'fade';
  const slideDuration = (advertising.slideDuration || 8) * 1000;
  const showSummary = advertising.showSummary !== false;

  // Construire la liste des slides à afficher
  const activeSlides = (advertising.slides || [])
    .filter(s => s.active)
    .sort((a, b) => (a.orderIndex || 0) - (b.orderIndex || 0));

  // Liste complète : résumé (optionnel) + slides médias
  const allSlides = [];
  if (showSummary) {
    allSlides.push({ type: 'summary' });
  }
  activeSlides.forEach(slide => {
    const type = slide.mediaType || 'image';
    // Une slide YouTube dont l'URL n'est pas intégrable ne doit PAS entrer dans la rotation :
    // elle occuperait l'écran le temps d'une slide en n'affichant rien. On la saute, la
    // publicité continue avec les autres, et la trace part en console pour le diagnostic.
    if (type === 'youtube' && !urlIntegrationYoutube(slide.url)) {
      console.warn('[publicité] URL YouTube inutilisable, slide ignorée :', slide.url);
      return;
    }
    allSlides.push({ type, ...slide });
  });

  // Si aucune slide, juste le résumé
  if (allSlides.length === 0) {
    allSlides.push({ type: 'summary' });
  }

  // Fetch les stats du jour
  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${serverUrl}/api/tickets/stats`);
      if (res.data.success) {
        // L'endpoint renvoie { stats: {...dailyStats}, counters }. On déballe `stats`
        // (repli sur la racine si l'ancien format plat est servi).
        setStats(res.data.data?.stats || res.data.data);
      }
    } catch (err) {
      console.warn('AdSlideshow: impossible de charger les stats', err);
    }
  }, [serverUrl]);

  useEffect(() => {
    fetchStats();
    // Rafraîchir les stats toutes les 2 minutes
    const interval = setInterval(fetchStats, 120000);
    return () => clearInterval(interval);
  }, [fetchStats]);

  // Avancer au slide suivant
  const nextSlide = useCallback(() => {
    setTransitioning(true);
    setTimeout(() => {
      setCurrentIndex(prev => (prev + 1) % allSlides.length);
      setTransitioning(false);
    }, transition === 'none' ? 0 : 500);
  }, [allSlides.length, transition]);

  // Timer de rotation des slides
  useEffect(() => {
    const current = allSlides[currentIndex];

    // Pour les vidéos locales, on attend `onEnded` — mais JAMAIS indéfiniment. Un fichier
    // absent, un codec que le lecteur refuse, un téléchargement interrompu : `onEnded` ne
    // vient pas, et le diaporama s'arrête pour de bon sur cette slide. L'écran de la salle
    // d'attente reste alors figé sans que rien ne l'explique. D'où ce filet de sécurité :
    // au-delà d'une durée large, on avance quand même.
    if (current?.type === 'video') {
      timerRef.current = setTimeout(nextSlide, Math.max(slideDuration, 60000));
      return () => clearTimeout(timerRef.current);
    }

    timerRef.current = setTimeout(nextSlide, slideDuration);
    return () => clearTimeout(timerRef.current);
  }, [currentIndex, slideDuration, nextSlide, allSlides]);

  // Quand une vidéo se termine, passer au slide suivant
  const handleVideoEnded = useCallback(() => {
    nextSlide();
  }, [nextSlide]);

  // Classes de transition
  const getTransitionStyle = () => {
    if (transition === 'none') return {};
    if (transition === 'fade') {
      return {
        opacity: transitioning ? 0 : 1,
        transition: 'opacity 0.5s ease-in-out'
      };
    }
    if (transition === 'slide') {
      return {
        transform: transitioning ? 'translateX(-100%)' : 'translateX(0)',
        transition: 'transform 0.5s ease-in-out'
      };
    }
    return {};
  };

  const currentSlide = allSlides[currentIndex] || allSlides[0];

  return (
    <main className="display-main" style={{ position: 'relative', overflow: 'hidden' }}>
      <div style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        ...getTransitionStyle()
      }}>
        {currentSlide.type === 'summary' && (
          <SummarySlide stats={stats} config={config} />
        )}

        {currentSlide.type === 'image' && (
          <div style={{ width: '100%', height: '100%', position: 'relative' }}>
            <img
              src={`${serverUrl}/uploads/ads/${currentSlide.filename}`}
              alt={currentSlide.title || 'Publicité'}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                backgroundColor: '#000'
              }}
            />
            {(currentSlide.title || currentSlide.titleAr) && (
              <SlideOverlay title={currentSlide.title} titleAr={currentSlide.titleAr} />
            )}
          </div>
        )}

        {currentSlide.type === 'video' && (
          <div style={{ width: '100%', height: '100%', position: 'relative' }}>
            <video
              ref={videoRef}
              src={`${serverUrl}/uploads/ads/${currentSlide.filename}`}
              autoPlay
              muted
              onEnded={handleVideoEnded}
              onError={handleVideoEnded}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                backgroundColor: '#000'
              }}
            />
            {(currentSlide.title || currentSlide.titleAr) && (
              <SlideOverlay title={currentSlide.title} titleAr={currentSlide.titleAr} />
            )}
          </div>
        )}

        {currentSlide.type === 'youtube' && (
          <div style={{ width: '100%', height: '100%', position: 'relative' }}>
            <iframe
              src={urlIntegrationYoutube(currentSlide.url) || 'about:blank'}
              title={currentSlide.title || 'YouTube'}
              style={{
                width: '100%',
                height: '100%',
                border: 'none'
              }}
              allow="autoplay; encrypted-media"
              allowFullScreen
            />
            {(currentSlide.title || currentSlide.titleAr) && (
              <SlideOverlay title={currentSlide.title} titleAr={currentSlide.titleAr} />
            )}
          </div>
        )}
      </div>

      {/* Indicateur de slides en bas */}
      {allSlides.length > 1 && (
        <div style={{
          position: 'absolute',
          bottom: '10px',
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          gap: '6px',
          zIndex: 10
        }}>
          {allSlides.map((_, i) => (
            <div key={i} style={{
              width: i === currentIndex ? '24px' : '8px',
              height: '8px',
              borderRadius: '4px',
              backgroundColor: i === currentIndex ? '#fff' : 'rgba(255,255,255,0.4)',
              transition: 'all 0.3s ease'
            }} />
          ))}
        </div>
      )}
    </main>
  );
};

/**
 * Slide résumé des stats du jour
 */
const SummarySlide = ({ stats, config }) => {
  const primaryColor = config?.ui?.primaryColor || '#2196F3';

  const items = [
    {
      label: 'Tickets du jour',
      labelAr: 'تذاكر اليوم',
      value: stats?.total || stats?.ticketsToday || 0,
      icon: '🎫'
    },
    {
      label: 'Traités',
      labelAr: 'تمت معالجتها',
      value: stats?.completed || stats?.completedTickets || 0,
      icon: '✅'
    },
    {
      label: 'En attente',
      labelAr: 'في الانتظار',
      value: stats?.waiting || stats?.waitingTickets || 0,
      icon: '⏳'
    },
    {
      label: 'Temps moyen',
      labelAr: 'متوسط الوقت',
      value: (stats?.averageWaitTime ?? stats?.avgWaitTime)
        ? `${Math.round(stats.averageWaitTime ?? stats.avgWaitTime)} min` : '--',
      icon: '⏱️'
    }
  ];

  return (
    <div style={{
      width: '100%',
      height: '100%',
      background: `linear-gradient(135deg, ${primaryColor}, ${primaryColor}dd)`,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '2rem',
      color: '#fff'
    }}>
      <h2 style={{ fontSize: '2.5rem', marginBottom: '0.5rem', fontWeight: 'bold' }}>
        Résumé du jour
      </h2>
      <p style={{ fontSize: '1.3rem', marginBottom: '2rem', opacity: 0.85 }} dir="rtl">
        ملخص اليوم
      </p>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: '1.5rem',
        maxWidth: '700px',
        width: '100%'
      }}>
        {items.map((item, i) => (
          <div key={i} style={{
            background: 'rgba(255,255,255,0.15)',
            borderRadius: '16px',
            padding: '1.5rem',
            textAlign: 'center',
            backdropFilter: 'blur(10px)'
          }}>
            <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>{item.icon}</div>
            <div style={{ fontSize: '2.8rem', fontWeight: 'bold', marginBottom: '0.3rem' }}>
              {item.value}
            </div>
            <div style={{ fontSize: '1.1rem', opacity: 0.9 }}>{item.label}</div>
            <div style={{ fontSize: '0.95rem', opacity: 0.7 }} dir="rtl">{item.labelAr}</div>
          </div>
        ))}
      </div>

      <div style={{ marginTop: '2rem', fontSize: '1rem', opacity: 0.6 }}>
        {config?.agency?.name || ''} — {new Date().toLocaleDateString('fr-FR')}
      </div>
    </div>
  );
};

/**
 * Overlay titre sur une slide image/vidéo/YouTube
 */
const SlideOverlay = ({ title, titleAr }) => (
  <div style={{
    position: 'absolute',
    bottom: '40px',
    left: 0,
    right: 0,
    textAlign: 'center',
    padding: '1rem 2rem',
    background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
    color: '#fff'
  }}>
    {title && <div style={{ fontSize: '1.8rem', fontWeight: 'bold' }}>{title}</div>}
    {titleAr && <div style={{ fontSize: '1.4rem', marginTop: '0.3rem' }} dir="rtl">{titleAr}</div>}
  </div>
);

export default AdSlideshow;
